/**
 * Slug generation utilities
 *
 * Provides functions for generating URL-safe slugs from connection and object names
 */

/**
 * Generate a URL-safe slug from a name
 *
 * @param {string} name - The name to convert to a slug
 * @returns {string} URL-safe slug
 *
 * @example
 * generateSlug('Sparvi Sandbox') // 'sparvi-sandbox'
 * generateSlug('My Database (Production)') // 'my-database-production'
 * generateSlug('Test_DB_123') // 'test-db-123'
 */
export const generateSlug = (name) => {
  if (!name) {
    return '';
  }

  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric chars with hyphens
    .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
};

/**
 * Generate a unique slug by appending a suffix if needed
 *
 * @param {string} baseSlug - The base slug to make unique
 * @param {Array<string>} existingSlugs - Array of existing slugs to check against
 * @returns {string} Unique slug
 *
 * @example
 * generateUniqueSlug('my-db', ['my-db']) // 'my-db-2'
 * generateUniqueSlug('my-db', ['my-db', 'my-db-2']) // 'my-db-3'
 */
export const generateUniqueSlug = (baseSlug, existingSlugs = []) => {
  if (!existingSlugs.includes(baseSlug)) {
    return baseSlug;
  }

  let counter = 2;
  let slug = `${baseSlug}-${counter}`;

  while (existingSlugs.includes(slug)) {
    counter++;
    slug = `${baseSlug}-${counter}`;
  }

  return slug;
};

/**
 * Add slug to a connection object
 *
 * @param {Object} connection - Connection object
 * @param {Array<Object>} allConnections - All connections (for uniqueness check)
 * @returns {Object} Connection object with slug property
 */
export const addSlugToConnection = (connection, allConnections = []) => {
  if (connection.slug) {
    return connection;
  }

  const baseSlug = generateSlug(connection.name);
  const existingSlugs = allConnections
    .filter(c => c.id !== connection.id && c.slug)
    .map(c => c.slug);

  return {
    ...connection,
    slug: generateUniqueSlug(baseSlug, existingSlugs)
  };
};

/**
 * Add slugs to an array of connections
 *
 * @param {Array<Object>} connections - Array of connection objects
 * @returns {Array<Object>} Connections with slugs
 */
export const addSlugsToConnections = (connections = []) => {
  const processed = [];

  connections.forEach(connection => {
    processed.push(addSlugToConnection(connection, processed.concat(connections)));
  });

  return processed;
};
